import { Combobox, Icon } from '@zillow/constellation-10';
import { Fragment, useContext } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox } from '@zg-rentals/ts-satellite-client';
import { logError } from '@zg-rentals/log-error';
import { SatelliteConversationReadRepliedStatusTypeV2 } from '@zg-rentals/ts-satellite-client';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import usePropertyFilter, { type UpdatePathWithListingAlias } from '../../../../hooks/usePropertyFilter';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { Skeleton, SkeletonListItem } from './Skeleton';
import { IsVisible } from '@zg-rentals/react-use';
import { IconSearchFilled } from '@zillow/constellation-icons';
import { buildRequestParams, getInboxConversationsQueryKey } from '../../../../hooks/useInboxConversationsQuery';

export default function PropertySearchBar() {
  const queryClient = useQueryClient();
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { clientTimeZone, folder, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);
  const filteredListingAlias = isByProperty && listingAlias ? listingAlias : undefined;

  const resetPropertyFilter = async () => {
    if (readRepliedStatus !== SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL) {
      // Exiting property scope drops any widened lookback (see changeFiltersOrFolders), so seed
      // the cache for the default-window query the unscoped list will actually run.
      const params = buildRequestParams({
        listingAlias: null,
        clientTimeZone,
        daysBack: null,
        folder,
        renterProgressStatus,
        readRepliedStatus,
      });

      try {
        const result = await SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox(params);
        queryClient.setQueryData(getInboxConversationsQueryKey(params), {
          pages: [result],
          pageParams: [undefined],
        });
      } catch (error) {
        logError({
          error,
          context: {
            details: 'PropertySearchBar - Error resetting property filter',
          },
        });
      }
    }

    await changeFiltersOrFolders({
      folder,
      readRepliedStatus,
      renterProgressStatus,
      byProperty: false,
      listingAlias: undefined,
    });
  };

  const updatePathWithListingAlias: UpdatePathWithListingAlias = async (listingAlias) => {
    await changeFiltersOrFolders({
      folder,
      readRepliedStatus,
      renterProgressStatus,
      byProperty: true,
      listingAlias,
    });
  };

  const {
    defaultListingAddress,
    errorMessage,
    handleChange,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    propertyListings,
    shouldShowSkeleton,
  } = usePropertyFilter({
    updatePathWithListingAlias,
    resetPropertyFilter,
    defaultListingAliasOnLoad: filteredListingAlias,
  });

  if (!errorMessage && shouldShowSkeleton) {
    return <Skeleton />;
  }

  return (
    <Combobox
      appearance="select"
      fluidDropdown
      focusFirstOption
      freeForm
      onChange={handleChange}
      onOptionSelect={handleSelectProperty}
      options={propertyListings}
      placeholder="Search by property address"
      selectCallbackFrequency="change"
      shouldAwaitInteractionResponse
      showLabelForValue
      size="md"
      value={defaultListingAddress || filteredListingAlias}
      renderAdornment={({ ref, ...rest }) => (
        <Combobox.Adornment ref={typeof ref === 'string' ? undefined : ref} {...rest}>
          <Icon render={<IconSearchFilled />} />
        </Combobox.Adornment>
      )}
      renderOption={({
        option,
        highlightPattern,
        key,
        ref,
        renderOptionLabel,
        renderOptionMeta,
        renderOptionMedia: _renderOptionMedia,
        ...rest
      }) => {
        if (typeof option !== 'object') return;

        const { value, label, meta } = option;
        const isLastItemInList = value === propertyListings[propertyListings.length - 1].value;

        return (
          <Fragment key={key}>
            <Combobox.Option option={option} ref={typeof ref === 'string' ? undefined : ref} {...rest}>
              {renderOptionLabel?.({ children: label, highlightPattern })}
              {meta && renderOptionMeta?.({ children: meta, highlightPattern })}
            </Combobox.Option>

            {isLastItemInList && hasMoreToPaginate && (
              <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px 200px 0px' }}>
                {({ ref }) => <SkeletonListItem ref={ref} />}
              </IsVisible>
            )}
          </Fragment>
        );
      }}
    />
  );
}
